import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import api, { formatApiError } from "@/lib/api";

export const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function CartDrawer() {
  const { items, updateQty, removeItem, subtotal, open, setOpen, clear } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const r = await api.post("/checkout", { lines: items.map((i) => ({ slug: i.slug, quantity: i.quantity })) });
      window.location.assign(r.data.checkoutUrl);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "SHOPIFY_NOT_CONFIGURED") {
        toast("Online checkout is going live soon", {
          description: "Call 970-650-5173 or use the quote form and we'll complete your order today.",
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-obsidian/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col border-l border-girder bg-steel"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-girder px-6 py-5">
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">Your Order</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-drawer-close" aria-label="Close cart" className="flex h-9 w-9 items-center justify-center border border-girder text-slate-400 hover:border-hivis hover:text-hivis">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                  <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Cart is empty</p>
                  <p className="mt-2 text-sm text-slate-400">Add a kit and get your banners standing up to the wind.</p>
                </div>
              ) : (
                <ul className="divide-y divide-girder">
                  {items.map((item) => (
                    <li key={item.slug} className="flex gap-4 py-4" data-testid={`cart-item-${item.slug}`}>
                      <div className="h-20 w-20 shrink-0 overflow-hidden clip-corner-sm bg-obsidian">
                        {item.image && <img src={item.image} alt={item.name} className="h-full w-full object-cover" />}
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-display text-lg font-bold uppercase leading-tight text-slate-100">{item.name}</p>
                          <button onClick={() => removeItem(item.slug)} data-testid={`cart-remove-${item.slug}`} aria-label={`Remove ${item.name}`} className="text-slate-500 transition-colors hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="mt-1 font-mono text-sm text-hivis">{fmt(item.price)}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <button onClick={() => updateQty(item.slug, item.quantity - 1)} data-testid={`cart-qty-minus-${item.slug}`} aria-label="Decrease quantity" className="flex h-7 w-7 items-center justify-center border border-girder text-slate-300 hover:border-hivis"><Minus className="h-3 w-3" /></button>
                          <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                          <button onClick={() => updateQty(item.slug, item.quantity + 1)} data-testid={`cart-qty-plus-${item.slug}`} aria-label="Increase quantity" className="flex h-7 w-7 items-center justify-center border border-girder text-slate-300 hover:border-hivis"><Plus className="h-3 w-3" /></button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-girder px-6 py-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-400">Subtotal</span>
                  <span className="font-display text-2xl font-black text-slate-100" data-testid="cart-subtotal">{fmt(subtotal)}</span>
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-500">Shipping calculated at checkout</p>
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="cart-checkout-button"
                  className="clip-corner mt-4 flex w-full items-center justify-center gap-2 bg-hivis py-4 font-display text-lg font-bold uppercase tracking-wide text-obsidian transition-colors duration-200 hover:bg-hivis-hover disabled:opacity-60"
                >
                  {checkingOut ? "Connecting..." : "Checkout"} <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={clear} data-testid="cart-clear-button" className="mt-2 w-full py-2 text-center font-mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-slate-300">
                  Clear order
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
